import { useState, Fragment } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import SchemaOrg from '@/components/feature/SchemaOrg';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError('');

    const form = e.currentTarget;
    const honeypotEl = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypotEl && honeypotEl.value.trim() !== '') {
      setFormStatus('success');
      form.reset();
      return;
    }

    const formData = new FormData(form);

    try {
      const response = await fetch('https://readdy.ai/api/form/d9qs29fnahb7382ovekg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText;
        setFormError(serverMsg || '提交失败，请稍后再试');
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <SEOHead
        title="关于我们"
        description="黑料社区品牌介绍：了解我们的使命、团队和口碑。黑料社区致力于为公众提供真实、可靠的爆料平台，数百万用户共同见证真相。"
        keywords="黑料社区,关于我们,品牌介绍,用户口碑,联系方式,爆料平台"
        canonicalUrl="https://heiliaoshequ.com/about"
      />
      <SchemaOrg type="WebPage" data={{
        name: '黑料社区 - 关于我们',
        url: 'https://heiliaoshequ.com/about',
        description: '了解黑料社区的品牌故事、用户口碑与联系方式。',
      }} />
      <SchemaOrg type="FAQPage" data={{
        mainEntity: [
          {
            '@type': 'Question',
            name: '黑料社区如何保证爆料的真实性？',
            acceptedAnswer: {
              '@type': 'Answer',
              text: '所有爆料内容均需经过实名认证用户提交，并通过平台AI+人工双重核验机制进行真实性筛查。我们要求爆料者提供可核验的证据材料，并对每条爆料进行多维度交叉验证，真实核验率高达99.7%。',
            },
          },
          {
            '@type': 'Question',
            name: '爆料者的个人信息是否安全？',
            acceptedAnswer: {
              '@type': 'Answer',
              text: '黑料社区提供严格的匿名保护机制。爆料者可以选择完全匿名发布，平台采用端到端加密技术保护用户隐私。我们承诺绝不向任何第三方透露爆料者身份信息。',
            },
          },
          {
            '@type': 'Question',
            name: '如何向黑料社区提交爆料？',
            acceptedAnswer: {
              '@type': 'Answer',
              text: '您可以通过本页面的联系表单、邮箱 tips@heiliaoshequ.com 或拨打热线 400-888-6666 提交爆料线索。我们的编辑团队会在24小时内对有价值的线索进行跟进。',
            },
          },
          {
            '@type': 'Question',
            name: '黑料社区是否会对爆料内容进行审核？',
            acceptedAnswer: {
              '@type': 'Answer',
              text: '是的，所有爆料内容在发布前都会经过严格的审核流程。我们的编辑团队会核实关键事实，确保内容符合法律法规和平台规范，同时保护爆料者权益和被爆料者的合法权利。',
            },
          },
          {
            '@type': 'Question',
            name: '如果我是被爆料者，发现有不实内容怎么办？',
            acceptedAnswer: {
              '@type': 'Answer',
              text: '我们设立了专门的申诉通道。如果您发现平台上有关于您的不实内容，可以通过邮箱 tips@heiliaoshequ.com 提交申诉，我们的法务团队会在3个工作日内处理。经核实确属不实内容的，我们将立即删除并公开更正。',
            },
          },
        ],
      }} />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <div className="container-main py-6">
            <Breadcrumb items={[
              { label: '首页', to: '/' },
              { label: '关于我们' },
            ]} />
          </div>

          <section className="py-14 bg-background-100">
            <div className="container-main">
              <div className="max-w-3xl mx-auto text-center animate-fade-in-up">
                <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950">
                  关于黑料社区
                </h1>
                <p className="mt-4 text-foreground-600 leading-relaxed">
                  黑料社区成立于2020年，是国内领先的独家爆料与内幕揭秘聚合平台。
                  我们致力于为公众提供一个安全、可靠、真实的爆料信息交流空间，
                  让每一个有价值的真相都能被看见。
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
                {[
                  { icon: 'ri-shield-star-line', title: '我们的使命', desc: '打破信息不对称，让真相不再被掩盖。我们相信每一个普通人都有权利了解那些被刻意隐藏的事实。' },
                  { icon: 'ri-eye-line', title: '我们的愿景', desc: '成为中国最值得信赖的爆料平台，构建公开透明的信息生态，让阳光照进每一个阴暗角落。' },
                  { icon: 'ri-heart-line', title: '核心价值观', desc: '真实、客观、公正。我们坚持实名核验机制，不发布未经证实的爆料，维护信息传播的严肃性和公信力。' },
                ].map((item, idx) => (
                  <div
                    key={item.title}
                    className="text-center p-8 rounded-lg bg-background-50 animate-fade-in-up"
                    style={{ animationDelay: `${idx * 100}ms` }}
                  >
                    <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-primary-100">
                      <i className={`${item.icon} text-2xl text-primary-500`}></i>
                    </div>
                    <h3 className="mt-4 text-lg font-heading font-bold text-foreground-900">{item.title}</h3>
                    <p className="mt-2 text-sm text-foreground-500 leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="py-14">
            <div className="container-main">
              <div className="text-center mb-10">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                  发展里程碑
                </h2>
                <p className="mt-2 text-sm text-foreground-500">从零到千万用户的成长之路</p>
              </div>

              <div className="max-w-3xl mx-auto">
                <div className="relative pl-8 border-l-2 border-primary-200 space-y-8">
                  {[
                    { year: '2020年3月', title: '黑料社区正式上线', desc: '由三位资深媒体人联合创立，首月注册用户突破10万。' },
                    { year: '2021年6月', title: '完成A轮融资', desc: '获得知名投资机构5000万元投资，团队扩展至50人。' },
                    { year: '2022年9月', title: '用户突破500万', desc: '月活跃用户突破500万，日均爆料投稿超过1000条。' },
                    { year: '2023年12月', title: '推出实名核验系统', desc: '自主研发的AI+人工双重核验系统上线，爆料真实率提升至99.7%。' },
                    { year: '2025年1月', title: '用户突破2000万', desc: '注册用户突破2000万，成为国内最大的爆料内容平台。' },
                  ].map((milestone, idx) => (
                    <div
                      key={milestone.year}
                      className="relative animate-fade-in-up"
                      style={{ animationDelay: `${idx * 80}ms` }}
                    >
                      <div className="absolute -left-[41px] w-6 h-6 rounded-full bg-primary-500 border-4 border-background-50"></div>
                      <div className="pl-6">
                        <span className="text-xs text-primary-500 font-semibold">{milestone.year}</span>
                        <h4 className="text-base font-heading font-bold text-foreground-900 mt-1">{milestone.title}</h4>
                        <p className="text-sm text-foreground-500 mt-1">{milestone.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          <section className="py-14 bg-background-100">
            <div className="container-main">
              <div className="text-center mb-10">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                  用户口碑
                </h2>
                <p className="mt-2 text-sm text-foreground-500">来听听社区用户怎么说</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-5 max-w-4xl mx-auto">
                {[
                  { name: '张先生', role: '资深媒体人', text: '黑料社区是我获取行业一手信息的最重要渠道。很多在其他平台上被删除的消息，在这里都能找到完整版本。真实性和时效性都令我印象深刻。', rating: 5 },
                  { name: '李女士', role: '企业法务', text: '作为法务人员，我经常需要了解行业动态和潜在风险。黑料社区提供的内幕信息虽然不是最"舒适"的内容，但确实帮助我发现了不少重要线索。', rating: 5 },
                  { name: '王同学', role: '大学生', text: '最开始是被同学推荐来吃瓜的，后来发现这里的内容远不止娱乐八卦。商业、科技领域的深度揭秘让我学到了很多学校不会教的东西。', rating: 4 },
                ].map((testimonial, idx) => (
                  <div
                    key={testimonial.name}
                    className="p-6 rounded-lg bg-background-50 animate-fade-in-up"
                    style={{ animationDelay: `${idx * 100}ms` }}
                  >
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <i
                          key={i}
                          className={`text-xs ${i < testimonial.rating ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'}`}
                        ></i>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed">"{testimonial.text}"</p>
                    <div className="flex items-center gap-3 mt-4 pt-4 border-t border-background-200/50">
                      <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                        <i className="ri-user-line text-primary-500 text-sm"></i>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-800">{testimonial.name}</p>
                        <p className="text-[10px] text-foreground-400">{testimonial.role}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="py-14">
            <div className="container-main">
              <div className="max-w-4xl mx-auto">
                <div className="text-center mb-10">
                  <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                    联系我们
                  </h2>
                  <p className="mt-2 text-sm text-foreground-500">有料要爆？有疑问要咨询？随时联系我们</p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
                  <div className="lg:col-span-2 space-y-5">
                    {[
                      { icon: 'ri-mail-line', title: '爆料邮箱', content: 'tips@heiliaoshequ.com' },
                      { icon: 'ri-phone-line', title: '客服热线', content: '400-888-6666' },
                      { icon: 'ri-map-pin-line', title: '公司地址', content: '北京市朝阳区建国路88号SOHO现代城A座15层' },
                      { icon: 'ri-time-line', title: '工作时间', content: '周一至周日 9:00 - 21:00' },
                    ].map((item) => (
                      <div
                        key={item.title}
                        className="flex items-start gap-4 p-4 rounded-lg bg-background-100"
                      >
                        <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 shrink-0">
                          <i className={`${item.icon} text-lg text-primary-500`}></i>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground-800">{item.title}</p>
                          <p className="text-sm text-foreground-500 mt-0.5">{item.content}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="lg:col-span-3">
                    <div className="rounded-lg bg-background-100 p-6">
                      <h3 className="text-lg font-heading font-bold text-foreground-900 mb-4">发送消息</h3>
                      <form data-readdy-form onSubmit={handleSubmit} className="space-y-4">
                        <div className="honeypot-wrapper">
                          <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label htmlFor="contact-name" className="block text-xs font-medium text-foreground-600 mb-1.5">姓名</label>
                            <input
                              type="text"
                              id="contact-name"
                              name="name"
                              required
                              className="w-full px-3 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors duration-200"
                              placeholder="请输入您的姓名"
                            />
                          </div>
                          <div>
                            <label htmlFor="contact-email" className="block text-xs font-medium text-foreground-600 mb-1.5">邮箱</label>
                            <input
                              type="email"
                              id="contact-email"
                              name="email"
                              required
                              className="w-full px-3 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors duration-200"
                              placeholder="请输入您的邮箱"
                            />
                          </div>
                        </div>
                        <div>
                          <label htmlFor="contact-subject" className="block text-xs font-medium text-foreground-600 mb-1.5">主题</label>
                          <input
                            type="text"
                            id="contact-subject"
                            name="subject"
                            required
                            className="w-full px-3 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors duration-200"
                            placeholder="请选择主题：爆料投稿 / 商务合作 / 投诉建议"
                          />
                        </div>
                        <div>
                          <label htmlFor="contact-message" className="block text-xs font-medium text-foreground-600 mb-1.5">消息内容</label>
                          <textarea
                            id="contact-message"
                            name="message"
                            required
                            maxLength={500}
                            rows={5}
                            className="w-full px-3 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors duration-200 resize-none"
                            placeholder="请详细描述您的爆料或咨询内容（最多500字）"
                          ></textarea>
                          <span className="text-[10px] text-foreground-400 mt-1 block">最多500字</span>
                        </div>
                        <button
                          type="submit"
                          className="w-full sm:w-auto px-8 py-3 text-sm font-medium rounded-full bg-primary-500 text-white hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap"
                        >
                          发送消息
                          <i className="ri-send-plane-line ml-2"></i>
                        </button>

                        {formStatus === 'success' && (
                          <div className="p-3 rounded-md bg-accent-50 text-accent-700 text-sm">
                            <i className="ri-checkbox-circle-line mr-1.5"></i>
                            消息已发送成功！我们会尽快与您联系。
                          </div>
                        )}
                        {formStatus === 'error' && (
                          <div className="p-3 rounded-md bg-primary-50 text-primary-700 text-sm">
                            <i className="ri-error-warning-line mr-1.5"></i>
                            {formError}
                          </div>
                        )}
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="py-14 bg-background-100">
            <div className="container-main">
              <div className="text-center mb-8">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                  常见问题
                </h2>
              </div>
              <div className="max-w-3xl mx-auto space-y-3">
                {[
                  { q: '黑料社区如何保证爆料的真实性？', a: '所有爆料内容均需经过实名认证用户提交，并通过平台AI+人工双重核验机制进行真实性筛查。我们要求爆料者提供可核验的证据材料，并对每条爆料进行多维度交叉验证。' },
                  { q: '爆料者的个人信息是否安全？', a: '黑料社区提供严格的匿名保护机制。爆料者可以选择完全匿名发布，平台采用端到端加密技术保护用户隐私。我们承诺绝不向任何第三方透露爆料者身份信息。' },
                  { q: '如何向黑料社区提交爆料？', a: '您可以通过本页面的联系表单、邮箱 tips@heiliaoshequ.com 或拨打热线 400-888-6666 提交爆料线索。我们的编辑团队会在24小时内对有价值的线索进行跟进。' },
                  { q: '如果发现不实内容怎么办？', a: '我们设立了专门的申诉通道。如果您发现平台上有不实内容，可以通过邮箱提交申诉，我们的法务团队会在3个工作日内处理。经核实确属不实内容的，我们将立即删除并公开更正。' },
                ].map((faq, idx) => (
                  <details
                    key={idx}
                    className="group rounded-lg bg-background-50 overflow-hidden"
                  >
                    <summary className="flex items-center justify-between p-5 cursor-pointer text-sm font-medium text-foreground-800 hover:text-foreground-950 transition-colors duration-200">
                      {faq.q}
                      <i className="ri-arrow-down-s-line text-lg group-open:rotate-180 transition-transform duration-200"></i>
                    </summary>
                    <div className="px-5 pb-5 text-sm text-foreground-500 leading-relaxed">
                      {faq.a}
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}